import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Edit, Delete, Inventory } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  image_url: string;
  category_id: number;
  stock_quantity: number;
  is_active: boolean;
}

interface Category {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

const ProductAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [productDialog, setProductDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [stockDialog, setStockDialog] = useState(false);
  const [stockProduct, setStockProduct] = useState<Product | null>(null);
  const [stockQuantity, setStockQuantity] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    image_url: '',
    category_id: '',
    stock_quantity: '',
    is_active: true,
  });
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/products');
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Product Dialog Handlers
  const openProductDialog = (product: Product | null = null) => {
    if (product) {
      setSelectedProduct(product);
      setProductForm({
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
        image_url: product.image_url || '',
        category_id: product.category_id?.toString() || '',
        stock_quantity: product.stock_quantity.toString(),
        is_active: product.is_active,
      });
    } else {
      setSelectedProduct(null);
      setProductForm({
        name: '',
        description: '',
        price: '',
        image_url: '',
        category_id: '',
        stock_quantity: '',
        is_active: true,
      });
    }
    setProductDialog(true);
  };

  const closeProductDialog = () => {
    setProductDialog(false);
    setSelectedProduct(null);
  };

  const handleProductChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProductForm({ ...productForm, [name]: value });
  };

  const handleProductCategoryChange = (e: SelectChangeEvent) => {
    setProductForm({ ...productForm, category_id: e.target.value });
  };

  const handleProductSubmit = async () => {
    try {
      const productData = {
        name: productForm.name,
        description: productForm.description,
        price: parseFloat(productForm.price),
        image_url: productForm.image_url,
        category_id: productForm.category_id ? parseInt(productForm.category_id) : null,
        stock_quantity: parseInt(productForm.stock_quantity),
        is_active: productForm.is_active,
      };

      const url = selectedProduct
        ? `https://salon.rockyroadsolutions.com/api/products/${selectedProduct.id}`
        : 'https://salon.rockyroadsolutions.com/api/products';
      
      const method = selectedProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (response.ok) {
        fetchProducts();
        closeProductDialog();
      }
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleQuickStockUpdate = (product: Product) => {
    setStockProduct(product);
    setStockQuantity(product.stock_quantity.toString());
    setStockDialog(true);
  };

  const handleStockUpdate = async () => {
    if (!stockProduct) return;
    
    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/products/${stockProduct.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          stock_quantity: parseInt(stockQuantity)
        }),
      });

      if (response.ok) {
        fetchProducts();
        setStockDialog(false);
        setStockProduct(null);
      }
    } catch (error) {
      console.error('Error updating stock:', error);
    }
  };

  const handleDeleteProduct = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/products/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchProducts();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  // Category Dialog Handlers
  const openCategoryDialog = (category: Category | null = null) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        is_active: category.is_active,
      });
    } else {
      setSelectedCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        is_active: true,
      });
    }
    setCategoryDialog(true);
  };

  const closeCategoryDialog = () => {
    setCategoryDialog(false);
    setSelectedCategory(null);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCategoryForm({ ...categoryForm, [name]: value });
  };

  const handleCategorySubmit = async () => {
    try {
      const categoryData = {
        name: categoryForm.name,
        description: categoryForm.description,
        is_active: categoryForm.is_active,
      };

      const url = selectedCategory
        ? `https://salon.rockyroadsolutions.com/api/categories/${selectedCategory.id}`
        : 'https://salon.rockyroadsolutions.com/api/categories';
      
      const method = selectedCategory ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(categoryData),
      });

      if (response.ok) {
        fetchCategories();
        closeCategoryDialog();
      }
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    try {
      const response = await fetch(`https://salon.rockyroadsolutions.com/api/categories/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        fetchCategories();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Error deleting category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Product Administration
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Products" />
        <Tab label="Categories" />
      </Tabs>

      {/* Products Tab */}
      {activeTab === 0 && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openProductDialog()}
              sx={{
                background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
              }}
            >
              Add Product
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      {categories.find(c => c.id === product.category_id)?.name || 'Uncategorized'}
                    </TableCell>
                    <TableCell>${typeof product.price === 'number' ? product.price.toFixed(2) : parseFloat(product.price).toFixed(2)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography sx={{ mr: 1 }}>{product.stock_quantity}</Typography>
                        <Button 
                          size="small" 
                          variant="outlined" 
                          onClick={() => handleQuickStockUpdate(product)}
                          sx={{ minWidth: 'auto', p: '2px 8px', fontSize: '0.75rem' }}
                        >
                          Update
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell>{product.is_active ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => openProductDialog(product)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteProduct(product.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Categories Tab */}
      {activeTab === 1 && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openCategoryDialog()}
              sx={{
                background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
              }}
            >
              Add Category
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>{category.description}</TableCell>
                    <TableCell>{category.is_active ? 'Active' : 'Inactive'}</TableCell>
                    <TableCell>
                      <IconButton onClick={() => openCategoryDialog(category)}>
                        <Edit />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteCategory(category.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Product Dialog */}
      <Dialog open={productDialog} onClose={closeProductDialog} maxWidth="md" fullWidth>
        <DialogTitle>{selectedProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="name"
                value={productForm.name}
                onChange={handleProductChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={productForm.category_id}
                  onChange={handleProductCategoryChange}
                  label="Category"
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={productForm.price}
                onChange={handleProductChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stock Quantity"
                name="stock_quantity"
                type="number"
                value={productForm.stock_quantity}
                onChange={handleProductChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                name="image_url"
                value={productForm.image_url}
                onChange={handleProductChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={4}
                value={productForm.description}
                onChange={handleProductChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProductDialog}>Cancel</Button>
          <Button
            onClick={handleProductSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
            }}
          >
            {selectedProduct ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialog} onClose={closeCategoryDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={categoryForm.name}
                onChange={handleCategoryChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={categoryForm.description}
                onChange={handleCategoryChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCategoryDialog}>Cancel</Button>
          <Button
            onClick={handleCategorySubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
            }}
          >
            {selectedCategory ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Stock Update Dialog */}
      <Dialog open={stockDialog} onClose={() => setStockDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Inventory sx={{ mr: 1 }} />
            Update Stock
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {stockProduct?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current stock: {stockProduct?.stock_quantity}
            </Typography>
            <TextField
              fullWidth
              label="New Stock Quantity"
              type="number"
              value={stockQuantity}
              onChange={(e) => setStockQuantity(e.target.value)}
              sx={{ mt: 2 }}
              InputProps={{
                inputProps: { min: 0 }
              }}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockDialog(false)}>Cancel</Button>
          <Button
            onClick={handleStockUpdate}
            variant="contained"
            color="primary"
          >
            Update Stock
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductAdmin;
